import {create} from 'zustand';
import {Product} from '../models/product.model';
import {Brand} from '../models/brand.model';
import {Tag} from '../models/tag.model';
import {ProductType} from '../models/productType.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {ProductFormData, ProductFormValidation} from '../models/ProductFormTypes';
import {productFactory} from './productFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

type ActiveTab = 'products' | 'classifications';
type ClassificationsSubTab = 'brands' | 'types' | 'tags';

interface ProductsState {
  // Data
  products: Product[];
  brands: Brand[];
  tags: Tag[];
  productTypes: ProductType[];
  pagination: Pagination | undefined;
  page: number;
  pageSize: number;

  // Loading states
  loading: boolean;
  brandsLoading: boolean;
  tagsLoading: boolean;
  typesLoading: boolean;

  // Error states
  error: string | null;
  brandsError: string | null;
  tagsError: string | null;
  typesError: string | null;

  // UI State
  activeTab: ActiveTab;
  classificationsSubTab: ClassificationsSubTab;
  isDetailsModalOpen: boolean;
  isAddStockFormOpen: boolean;
  isAddProductModalOpen: boolean;
  isEditProductModalOpen: boolean;
  selectedProductForDetails: Product | null;
  selectedProductForStock: Product | null;
  editProductId: number | null;
  searchQuery: string;

  // Form State
  formData: ProductFormData;
  isSubmitting: boolean;
  isValid: boolean;
  isDirty: boolean;
  errors: Record<string, string>;
  imagePreviews: string[];

  // Data Actions
  fetchProducts: (page?: number, limit?: number) => Promise<void>;
  fetchBrands: () => Promise<void>;
  fetchTags: () => Promise<void>;
  fetchProductTypes: () => Promise<void>;
  createProduct: (formData: FormData) => Promise<Product>;
  updateProduct: (id: number, formData: FormData) => Promise<Product>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;

  // UI Actions
  setActiveTab: (tab: ActiveTab) => void;
  setClassificationsSubTab: (subTab: ClassificationsSubTab) => void;
  openDetailsModal: () => void;
  closeDetailsModal: () => void;
  openAddStockForm: () => void;
  closeAddStockForm: () => void;
  openAddProductModal: () => void;
  closeAddProductModal: () => void;
  openEditProductModal: () => void;
  closeEditProductModal: () => void;
  setSelectedProductForDetails: (product: Product | null) => void;
  setSelectedProductForStock: (product: Product | null) => void;
  setEditProductId: (id: number | null) => void;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  reset: () => void;

  // Form Actions
  setField: (field: keyof ProductFormData, value: any) => void;
  setFields: (fields: Partial<ProductFormData>) => void;
  resetForm: () => void;
  setError: (field: string, error: string) => void;
  setErrors: (errors: Record<string, string>) => void;
  clearFormErrors: () => void;
  clearError: (field: string) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setValid: (isValid: boolean) => void;
  setDirty: (isDirty: boolean) => void;
  setImagePreviews: (previews: string[]) => void;
  addImagePreview: (preview: string) => void;
  removeImagePreview: (index: number) => void;
  clearImagePreviews: () => void;
}

// Initial form data
const initialFormData: ProductFormData = {
  name: '',
  type: '',
  brandId: '',
  selectedTag: '',
  price: '',
  barcode: '',
  hasSerial: false,
  quantity: '',
  images: [],
  serials: []
};

export const useProductsStore = create<ProductsState>((set, get) => {
  // Initialize use cases using factory
  const useCases = productFactory.getAllUseCases();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  return {
    // Initial data state
    products: [],
    brands: [],
    tags: [],
    productTypes: [],
    pagination: undefined,
    page: 1,
    pageSize: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
    loading: false,
    brandsLoading: false,
    tagsLoading: false,
    typesLoading: false,
    error: null,
    brandsError: null,
    tagsError: null,
    typesError: null,

    // Initial UI state
    activeTab: 'products',
    classificationsSubTab: 'brands',
    isDetailsModalOpen: false,
    isAddStockFormOpen: false,
    isAddProductModalOpen: false,
    isEditProductModalOpen: false,
    selectedProductForDetails: null,
    selectedProductForStock: null,
    editProductId: null,
    searchQuery: '',

    // Initial form state
    formData: initialFormData,
    isSubmitting: false,
    isValid: true,
    isDirty: false,
    errors: {},
    imagePreviews: [],

    // Data Actions
    fetchProducts: async (page = get().page, limit = get().pageSize) => {
      set({loading: true, error: null});
      try {
        const {items, pagination} = await useCases.getAllProducts.execute(page, limit);
        set({products: items, pagination, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err);
      }
    },

    fetchBrands: async () => {
      set({brandsLoading: true, brandsError: null});
      try {
        const brands = await useCases.getAllBrands.execute();
        set({brands, brandsLoading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({brandsError: appError.message, brandsLoading: false});
        errorHandlingService.handleError(err);
      }
    },

    fetchTags: async () => {
      set({tagsLoading: true, tagsError: null});
      try {
        const tags = await useCases.getAllTags.execute();
        set({tags, tagsLoading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({tagsError: appError.message, tagsLoading: false});
        errorHandlingService.handleError(err);
      }
    },

    fetchProductTypes: async () => {
      set({typesLoading: true, typesError: null});
      try {
        const productTypes = await useCases.getAllProductTypes.execute();
        set({productTypes, typesLoading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({typesError: appError.message, typesLoading: false});
        errorHandlingService.handleError(err);
      }
    },

    createProduct: async (formData: FormData) => {
      try {
        const newProduct = await useCases.createProduct.execute(formData);
        await get().refreshData();
        return newProduct;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateProduct: async (id: number, formData: FormData) => {
      try {
        const updated = await useCases.updateProduct.execute(id, formData);
        await get().refreshData();
        return updated;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    setPage: (page: number) => {
      set({page});
      get().fetchProducts(page);
    },

    setPageSize: (pageSize: number) => {
      set({pageSize});
      get().fetchProducts(get().page, pageSize);
    },

    refreshData: async () => {
      await Promise.all([
        get().fetchProducts(),
        get().fetchBrands(),
        get().fetchTags(),
        get().fetchProductTypes()
      ]);
    },

    clearErrors: () => {
      set({error: null, brandsError: null, tagsError: null, typesError: null});
    },

    // UI Actions
    setActiveTab: (tab) => set({activeTab: tab}),
    setClassificationsSubTab: (subTab) => set({classificationsSubTab: subTab}),
    openDetailsModal: () => set({isDetailsModalOpen: true}),
    closeDetailsModal: () => set({isDetailsModalOpen: false}),
    openAddStockForm: () => set({isAddStockFormOpen: true}),
    closeAddStockForm: () => set({isAddStockFormOpen: false}),
    openAddProductModal: () => set({isAddProductModalOpen: true}),
    closeAddProductModal: () => set({isAddProductModalOpen: false}),
    openEditProductModal: () => set({isEditProductModalOpen: true}),
    closeEditProductModal: () => set({isEditProductModalOpen: false}),
    setSelectedProductForDetails: (product) => set({selectedProductForDetails: product}),
    setSelectedProductForStock: (product) => set({selectedProductForStock: product}),
    setEditProductId: (id) => set({editProductId: id}),
    setSearchQuery: (query) => set({searchQuery: query}),
    clearSearch: () => set({searchQuery: ''}),
    reset: () => set({
      activeTab: 'products',
      classificationsSubTab: 'brands',
      isDetailsModalOpen: false,
      isAddStockFormOpen: false,
      isAddProductModalOpen: false,
      isEditProductModalOpen: false,
      selectedProductForDetails: null,
      selectedProductForStock: null,
      editProductId: null,
      searchQuery: ''
    }),

    // Form Actions
    setField: (field, value) => {
      set((state) => ({
        formData: {
          ...state.formData,
          [field]: value
        },
        isDirty: true
      }));

      // Clear error for this field when user starts typing
      const {errors} = get();
      if (errors[field]) {
        get().clearError(field);
      }
    },

    setFields: (fields) => {
      set((state) => ({
        formData: {
          ...state.formData,
          ...fields
        },
        isDirty: true
      }));
    },

    resetForm: () => {
      set({
        formData: initialFormData,
        isSubmitting: false,
        isValid: true,
        isDirty: false,
        errors: {},
        imagePreviews: []
      });
    },


    setError: (field, error) => {
      set((state) => ({
        errors: {
          ...state.errors,
          [field]: error
        },
        isValid: false
      }));
    },

    setErrors: (errors) => {
      set({
        errors,
        isValid: Object.keys(errors).length === 0
      });
    },

    clearFormErrors: () => {
      set({
        errors: {},
        isValid: true
      });
    },

    clearError: (field) => {
      set((state) => {
        const newErrors = {...state.errors};
        delete newErrors[field];
        return {
          errors: newErrors,
          isValid: Object.keys(newErrors).length === 0
        };
      });
    },

    setSubmitting: (isSubmitting) => {
      set({isSubmitting});
    },

    setValid: (isValid) => {
      set({isValid});
    },

    setDirty: (isDirty) => {
      set({isDirty});
    },

    setImagePreviews: (previews) => {
      set({imagePreviews: previews});
    },

    addImagePreview: (preview) => {
      set((state) => ({
        imagePreviews: [...state.imagePreviews, preview]
      }));
    },

    removeImagePreview: (index) => {
      set((state) => ({
        imagePreviews: state.imagePreviews.filter((_, i) => i !== index)
      }));
    },

    clearImagePreviews: () => {
      set({imagePreviews: []});
    }
  };
});
